import React, { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { NumericFormat } from 'react-number-format';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { ruleActions } from '@apply/adminStore';
import { connect, useSelector } from 'react-redux';
import { InformModal } from '../../common/SharedComponents';
import { BfiCustomStore, hasPermission, RefreshEvents } from '@apply/ui';
import { ExperienceApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RuleChangeHistoryGrid } from '../common/components/RuleChangeHistoryGrid';
import { RuleChangeReasonModal } from './RuleChangeReasonModal';
import { RuleCategorizationTypeOptions } from '../common/constants/RuleCategorizationTypeOptions';
import { StructureTypeOptions } from '../../loans/common/LoanConstants';
import { RuleCategorizationType } from '../common/constants/RuleCategorizationType';
import { ExperienceRoleType, ExperienceRoleTypeOptions } from '@apply/constants';
import { PermissionType } from '@apply/constants';
import { handleAdminSuccess } from '../../common/SharedFunctions';
import { LoadPanel } from 'devextreme-react';

const styles = {
    buttonMarginRight: { marginRight: '10px' },
    buttonContainer: { marginRight: '20px', textAlign: 'right' },
    form: { paddingLeft: '5px', paddingTop: '10px' },
    formRow: { marginBottom: '0' },
    parent: {
        position: 'relative',
        weight: '750px',
    },
    cover: {
        backgroundColor: 'rgba(0,0,0,0.4)',
        position: 'absolute',
        height: '100%',
        width: '100%',
        display: 'block',
        zIndex: '999',
        textAlign: 'center',
        cursor: 'wait',
    },
    messageBox: {
        backgroundColor: 'white',
        display: 'inline-block',
        width: '65%',
        height: '100px',
        marginTop: '200px',
        padding: '20px',
        borderRadius: '10px',
        cursor: 'wait',
    },
    addButton: { height: '38px', float: 'left', marginLeft: '5px' },
    sectionHeader: { margin: '5px 10px' },
};

const RuleModal = () => {
    const { isOpen, ruleId, onSave } = useSelector((state) => state.rules.ruleModal);
    const { dispatch } = store;
    const [rule, set_rule] = useState(null);

    const [ruleBorrowerTypeId, set_ruleBorrowerTypeId] = useState('');
    const [roleTypeId, set_roleTypeId] = useState('');
    const [ruleCategorizationTypeId, set_ruleCategorizationTypeId] = useState('');
    const [propertyStructureTypeId, set_propertyStructureTypeId] = useState(null);
    const [groundUpConstructionCredits, set_groundUpConstructionCredits] = useState(null);
    const [buildRentCredits, set_buildRentCredits] = useState(null);
    const [majorRehabCredits, set_majorRehabCredits] = useState(null);
    const [lightRehabCredits, set_lightRehabCredits] = useState(null);
    const [rentalCredits, set_rentalCredits] = useState(null);

    const [loadingMessage, set_loadingMessage] = useState('');
    const [openNamesNotUnique, set_openNamesNotUnique] = useState(false);
    const [changesModalOpen, set_changesModalOpen] = useState(false);

    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);
    const refreshEvents = new RefreshEvents();
    const historyStore = new BfiCustomStore();

    const canEdit = hasPermission(PermissionType.ExperienceEditor);

    const _handleClose = () => dispatch(ruleActions.setRuleModal({ ruleModal: { isOpen: false } }));

    const _validateSubmission = async (e) => {
        e.preventDefault();

        if (!canEdit) {
            handleAdminWarning('You do not have rights to edit this information');
            return;
        }

        const hasChanges = hasOutstandingRuleChanges();
        if (hasChanges) {
            //Display modal and allow user to enters reason for change
            set_changesModalOpen(true);
            return;
        }
    };

    const hasOutstandingRuleChanges = () => {
        let hasChanges = false;

        if (rule.roleTypeId === ExperienceRoleType.TwentyPercentPlusOwner) {
            if (rule.ruleCategorizationTypeId === RuleCategorizationType.Residential) {
                hasChanges =
                    rule.ruleBorrowerTypeId !== ruleBorrowerTypeId ||
                    rule.roleTypeId !== roleTypeId ||
                    rule.ruleCategorizationTypeId !== ruleCategorizationTypeId ||
                    rule.propertyStructureTypeId !== propertyStructureTypeId ||
                    rule.groundUpConstructionCredits !== groundUpConstructionCredits ||
                    rule.buildRentCredits !== buildRentCredits ||
                    rule.majorRehabCredits !== majorRehabCredits ||
                    rule.lightRehabCredits !== lightRehabCredits ||
                    rule.rentalCredits !== rentalCredits;
            } else if (rule.ruleCategorizationTypeId === RuleCategorizationType.Commercial) {
                hasChanges =
                    rule.ruleBorrowerTypeId !== ruleBorrowerTypeId ||
                    rule.roleTypeId !== roleTypeId ||
                    rule.ruleCategorizationTypeId !== ruleCategorizationTypeId ||
                    rule.groundUpConstructionCredits !== groundUpConstructionCredits ||
                    rule.buildRentCredits !== buildRentCredits;
            }
        } else if (rule.roleTypeId === ExperienceRoleType.GeneralContractor) {
            hasChanges =
                rule.ruleBorrowerTypeId !== ruleBorrowerTypeId ||
                rule.roleTypeId !== roleTypeId ||
                rule.ruleCategorizationTypeId !== ruleCategorizationTypeId ||
                rule.propertyStructureTypeId !== propertyStructureTypeId ||
                rule.groundUpConstructionCredits !== groundUpConstructionCredits ||
                rule.buildRentCredits !== buildRentCredits ||
                rule.majorRehabCredits !== majorRehabCredits;
        }

        if (hasChanges) {
            return true;
        }

        return hasChanges;
    };

    const _saveRule = async (changeModel) => {
        try {
            if (!canEdit) {
                handleAdminWarning('You do not have rights to edit this information');
                return;
            }

            set_loadingMessage('Saving...');

            const model = {
                id: ruleId,
                ruleBorrowerTypeId,
                roleTypeId,
                ruleCategorizationTypeId,
                propertyStructureTypeId,
                groundUpConstructionCredits,
                buildRentCredits,
                majorRehabCredits,
                lightRehabCredits,
                rentalCredits,
                changeSummary: changeModel,
            };

            const response = await experienceApi.addUpdateRule(model);
            if (response.statusCode !== 200) {
                return;
            }

            const savedRule = response.result;
            if (savedRule.id <= 0) {
                throw new Error('Failed to save new Rule!');
            }
            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Rule successfully saved.');

            if (onSave) {
                onSave(savedRule.id);
            }

            setTimeout(() => {
                dispatch(ruleActions.setRuleModal({ ruleModal: { isOpen: false } }));
            }, 800);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const resetState = () => {
        set_rule(null);
        set_ruleBorrowerTypeId('');
        set_roleTypeId('');
        set_ruleCategorizationTypeId('');
        set_propertyStructureTypeId('');
        set_groundUpConstructionCredits(null);
        set_buildRentCredits(null);
        set_majorRehabCredits(null);
        set_lightRehabCredits(null);
        set_rentalCredits(null);
    };

    useEffect(() => {
        async function isOpenChanged() {
            resetState();

            if (!isOpen) {
                return;
            }

            try {
                set_loadingMessage('Loading...');
                let rule = null;
                if (ruleId && ruleId > 0) {
                    const response = await experienceApi.getRule(ruleId);
                    if (response.statusCode !== 200) {
                        return;
                    }
                    rule = response.result;
                }

                if (!rule) {
                    rule = {
                        ruleBorrowerTypeId: '',
                        roleTypeId: '',
                        ruleCategorizationId: '',
                        propertyStructureTypeId: '',
                        groundUpConstructionCredits: '',
                        buildRentCredits: '',
                        majorRehabCredits: '',
                        lightRehabCredits: '',
                        rentalCredits: '',
                    };
                }

                const clone = JSON.parse(JSON.stringify(rule));
                set_rule(clone);

                set_ruleBorrowerTypeId(rule.ruleBorrowerTypeId ?? '');
                set_roleTypeId(rule.roleTypeId ?? '');
                set_ruleCategorizationTypeId(rule.ruleCategorizationTypeId ?? '');
                set_propertyStructureTypeId(rule.propertyStructureTypeId ?? '');
                set_groundUpConstructionCredits(rule.groundUpConstructionCredits ?? '');
                set_buildRentCredits(rule.buildRentCredits ?? '');
                set_majorRehabCredits(rule.majorRehabCredits ?? '');
                set_lightRehabCredits(rule.lightRehabCredits ?? '');
                set_rentalCredits(rule.rentalCredits ?? '');
            } finally {
                set_loadingMessage('');
            }
        }

        isOpenChanged();
    }, [isOpen, ruleId]);

    const onChangesModalCancel = () => {
        set_changesModalOpen(false);
    };

    const onChangesModalSubmit = async (model) => {
        set_changesModalOpen(false);
        await _saveRule(model);
    };

    if (!rule) {
        return null;
    }

    return (
        <Dialog
            maxWidth='sm'
            open={isOpen}
            onClose={_handleClose}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '600px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div style={styles.parent}>
                                <form
                                    name='ruleForm'
                                    onSubmit={(event) => _validateSubmission(event)}
                                >
                                    <div className='scrollable-content'>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1>Experience Rule</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='roletype'>
                                                        Ruleset
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='roletype'
                                                        value={roleTypeId}
                                                        disabled={true}
                                                        onChange={(event) => set_roleTypeId(event.target.value)}
                                                        required
                                                    >
                                                        <option value=''>Select</option>
                                                        {ExperienceRoleTypeOptions.map((type) => {
                                                            return (
                                                                <option
                                                                    key={type.id}
                                                                    value={type.id}
                                                                >
                                                                    {type.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='ruleCategory'>
                                                        Rule Category
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='ruleCategory'
                                                        value={ruleCategorizationTypeId}
                                                        disabled={true}
                                                        onChange={(event) => set_ruleCategorizationTypeId(event.target.value)}
                                                        required
                                                    >
                                                        <option value=''>Select</option>
                                                        {RuleCategorizationTypeOptions.map((type) => {
                                                            return (
                                                                <option
                                                                    key={type.id}
                                                                    value={type.id}
                                                                >
                                                                    {type.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                {ruleCategorizationTypeId !== RuleCategorizationType.Commercial && (
                                                    <div className='columns small-12 large-6'>
                                                        <label htmlFor='propertyStructureType'>
                                                            Property Type
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <select
                                                            id='propertyStructureType'
                                                            value={propertyStructureTypeId}
                                                            disabled={true}
                                                            onChange={(event) => set_propertyStructureTypeId(event.target.value)}
                                                            required
                                                        >
                                                            <option value=''>Select</option>
                                                            {StructureTypeOptions.map((type) => {
                                                                return (
                                                                    <option
                                                                        key={type.id}
                                                                        value={type.id}
                                                                    >
                                                                        {type.name}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='groundUpConstructionUnits'>
                                                        Ground-Up Construction Credits
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <NumericFormat
                                                        id='groundUpConstructionUnits'
                                                        onValueChange={(values) => {
                                                            set_groundUpConstructionCredits(values.floatValue);
                                                        }}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        value={groundUpConstructionCredits}
                                                        required
                                                        displayType='input'
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value >= 0;
                                                        }}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='buildRentCredits'>
                                                        Build-Rent Credits
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <NumericFormat
                                                        id='buildRentCredits'
                                                        onValueChange={(values) => {
                                                            set_buildRentCredits(values.floatValue);
                                                        }}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        value={buildRentCredits}
                                                        required
                                                        displayType='input'
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value >= 0;
                                                        }}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                                {ruleCategorizationTypeId !== RuleCategorizationType.Commercial && (
                                                    <>
                                                        <div className='columns small-12 medium-6 large-6'>
                                                            <label htmlFor='majorRehabCredits'>
                                                                Major Rehab Credits
                                                                <span className='req'>*</span>
                                                            </label>
                                                            <NumericFormat
                                                                id='majorRehabCredits'
                                                                onValueChange={(values) => {
                                                                    set_majorRehabCredits(values.floatValue);
                                                                }}
                                                                decimalScale={2}
                                                                allowNegative={false}
                                                                value={majorRehabCredits}
                                                                required
                                                                displayType='input'
                                                                isAllowed={(values, sourceInfo) => {
                                                                    const value = values?.floatValue;
                                                                    return value === undefined || value === null || value >= 0;
                                                                }}
                                                                disabled={!canEdit}
                                                            />
                                                        </div>
                                                        {roleTypeId !== ExperienceRoleType.GeneralContractor && (
                                                            <>
                                                                <div className='columns small-12 medium-6 large-6'>
                                                                    <label htmlFor='lightRehabCredits'>
                                                                        Light Rehab Credits
                                                                        <span className='req'>*</span>
                                                                    </label>
                                                                    <NumericFormat
                                                                        id='lightRehabCredits'
                                                                        onValueChange={(values) => {
                                                                            set_lightRehabCredits(values.floatValue);
                                                                        }}
                                                                        decimalScale={2}
                                                                        allowNegative={false}
                                                                        value={lightRehabCredits}
                                                                        required
                                                                        displayType='input'
                                                                        isAllowed={(values, sourceInfo) => {
                                                                            const value = values?.floatValue;
                                                                            return (
                                                                                value === undefined ||
                                                                                value === null ||
                                                                                value >= 0
                                                                            );
                                                                        }}
                                                                        disabled={!canEdit}
                                                                    />
                                                                </div>
                                                                <div className='columns small-12 medium-6 large-6'>
                                                                    <label htmlFor='rentalCredits'>
                                                                        Rental Credits
                                                                        <span className='req'>*</span>
                                                                    </label>
                                                                    <NumericFormat
                                                                        id='rentalCredits'
                                                                        onValueChange={(values) => {
                                                                            set_rentalCredits(values.floatValue);
                                                                        }}
                                                                        decimalScale={2}
                                                                        allowNegative={false}
                                                                        value={rentalCredits}
                                                                        required
                                                                        displayType='input'
                                                                        isAllowed={(values, sourceInfo) => {
                                                                            const value = values?.floatValue;
                                                                            return (
                                                                                value === undefined ||
                                                                                value === null ||
                                                                                value >= 0
                                                                            );
                                                                        }}
                                                                        disabled={!canEdit}
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <div
                                                style={{
                                                    padding: '0px 10px 0 10px',
                                                }}
                                            >
                                                <RuleChangeHistoryGrid
                                                    ruleId={ruleId}
                                                    gridStore={historyStore}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            disabled={!canEdit}
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                                <RuleChangeReasonModal
                                    isOpen={changesModalOpen}
                                    onCancel={onChangesModalCancel}
                                    onSubmit={onChangesModalSubmit}
                                />
                                <InformModal
                                    isOpen={openNamesNotUnique}
                                    title='Duplicate Rule!'
                                    text='The Rule name that you have entered already exists in the database.'
                                    confirmCallback={() => set_openNamesNotUnique(false)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </Dialog>
    );
};

export default RuleModal;
